import React, { useState, useEffect } from 'react';
import { Container, Box, Fade, CircularProgress, Typography, useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Helmet } from 'react-helmet';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import LimitedTierlistHeader from './LimitedTierlistHeader';
import LimitedTierlistFilters from './LimitedTierlistFilters';
import LimitedTierlistStats from './LimitedTierlistStats';
import LimitedTierlistTable from './LimitedTierlistTable';
import LimitedTierlistPagination from './LimitedTierlistPagination';
import CardPreviewModal from './CardPreviewModal';
import MobileCardPreview from './MobileCardPreview';
import { useLimitedTierlistData } from './hooks/useLimitedTierlistData';

const LimitedTierlist = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
    const [mobilePreviewIndex, setMobilePreviewIndex] = useState(0);
    const [viewMode, setViewMode] = useState('table'); // 'table' lub 'tierlist'

    const {
        cards,
        filteredCards,
        loading,
        error,
        searchTerm,
        selectedRarity,
        selectedInkColor,
        selectedSet,
        selectedCost,
        showFilters,
        hoveredCard,
        hoverPosition,
        sortConfig,
        currentPage,
        cardsPerPage,
        totalPages,
        currentCards,
        uniqueSets,
        uniqueRarities,
        uniqueCosts,
        setSearchTerm,
        setSelectedRarity,
        setSelectedInkColor,
        setSelectedSet,
        setSelectedCost,
        setShowFilters,
        setHoveredCard,
        setHoverPosition,
        setSortConfig,
        setCurrentPage,
        commonUncommonOnly,
        setCommonUncommonOnly,
        handleCardHover,
        handleCardLeave,
        handlePageChange
    } = useLimitedTierlistData();

    const handleMobileCardClick = (card, index) => {
        if (isMobile) {
            // Znajdź indeks karty w całej liście filteredCards
            const globalIndex = filteredCards.findIndex(c => c.id === card.id);
            setMobilePreviewIndex(globalIndex >= 0 ? globalIndex : 0);
            setMobilePreviewOpen(true);
        }
    };

    const handleMobilePreviewClose = () => {
        setMobilePreviewOpen(false);
    };

    const handleMobileIndexChange = (newIndex) => {
        setMobilePreviewIndex(newIndex);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h6" color="error" textAlign="center">
                    {error}
                </Typography>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Limited Tierlist - playLorcana.pl</title>
                <meta name="description" content="Limited tierlist for Disney Lorcana - discover the best cards and build powerful decks" />
                <meta property="og:title" content="Limited Tierlist - playLorcana.pl" />
                <meta property="og:description" content="Limited tierlist for Disney Lorcana" />
            </Helmet>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Fade in timeout={800}>
                    <Box>
                        <LimitedTierlistHeader
                            totalCards={cards.length}
                            filteredCards={filteredCards.length}
                            currentPage={currentPage}
                            totalPages={totalPages}
                        />

                        <LimitedTierlistFilters
                            searchTerm={searchTerm}
                            selectedRarity={selectedRarity}
                            selectedInkColor={selectedInkColor}
                            selectedSet={selectedSet}
                            selectedCost={selectedCost || 'all'}
                            showFilters={showFilters}
                            uniqueSets={uniqueSets}
                            uniqueRarities={uniqueRarities}
                            uniqueCosts={uniqueCosts}
                            commonUncommonOnly={commonUncommonOnly}
                            onSearchChange={setSearchTerm}
                            onRarityChange={setSelectedRarity}
                            onInkColorChange={setSelectedInkColor}
                            onSetChange={setSelectedSet}
                            onCostChange={setSelectedCost}
                            onToggleFilters={() => setShowFilters(!showFilters)}
                            onCommonUncommonChange={setCommonUncommonOnly}
                        />

                        <LimitedTierlistStats cards={filteredCards} />

                        {/* View mode toggle */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => {
                                    if (newMode !== null) {
                                        setViewMode(newMode);
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(76, 29, 149, 0.3)',
                                    borderRadius: '12px',
                                    '& .MuiToggleButton-root': {
                                        color: 'white',
                                        border: 'none',
                                        px: 3,
                                        py: 1,
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.5)',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(76, 29, 149, 0.6)'
                                            }
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="table" aria-label="table view">
                                    <TableChartIcon sx={{ mr: 1 }} />
                                    Tabela
                                </ToggleButton>
                                <ToggleButton value="tierlist" aria-label="tierlist view">
                                    <ViewModuleIcon sx={{ mr: 1 }} />
                                    Tierlist
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <LimitedTierlistTable
                            cards={viewMode === 'tierlist' ? filteredCards : currentCards}
                            sortConfig={sortConfig}
                            onSort={setSortConfig}
                            onCardHover={isMobile ? () => { } : handleCardHover}
                            onCardLeave={isMobile ? () => { } : handleCardLeave}
                            onCardClick={isMobile ? handleMobileCardClick : undefined}
                            viewMode={viewMode}
                        />

                        {/* Pagination - tylko w widoku tabeli */}
                        {viewMode === 'table' && (
                            <LimitedTierlistPagination
                                totalPages={totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </Box>
                </Fade>

                {/* Desktop card preview */}
                {!isMobile && (
                    <CardPreviewModal
                        card={hoveredCard}
                        position={hoverPosition}
                        onClose={handleCardLeave}
                    />
                )}

                {/* Mobile card preview */}
                {isMobile && mobilePreviewOpen && (
                    <MobileCardPreview
                        cards={currentCards}
                        currentIndex={mobilePreviewIndex}
                        onClose={handleMobilePreviewClose}
                        onIndexChange={handleMobileIndexChange}
                    />
                )}
            </Container>
        </>
    );
};

export default LimitedTierlist; 